import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";import { Plus, ClipboardCheck, FileSearch, Users, Heart, ClipboardList, ShieldCheck, MapPinned, MapPin, FileDown, RotateCcw, Gift, Copy, Check, KeyRound, UserPlus, Sparkles, Trash2, Mail } from "lucide-react";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { CATEGORIES } from "@/data/constants";
import TierBadge from "@/components/TierBadge";
import { API, formatApiErrorDetail } from "@/lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "admin") return <AdminDashboard />;
  if (user?.role === "inspector") return <InspectorDashboard />;
  return <MemberDashboard />;
}

function MemberDashboard() {
  const { user } = useAuth();
  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <h1 className="font-heading text-4xl font-bold text-gray-900">Welcome, {user.name}</h1>
      <p className="mt-2 text-gray-700">Nominate places you know are accessible — help others plan with confidence.</p>
      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <Card className="border-2 border-gray-900 rounded-xl">
          <CardContent className="p-8">
            <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center"><Plus className="w-6 h-6"/></div>
            <h2 className="mt-4 font-heading text-2xl font-semibold">Nominate a place</h2>
            <p className="mt-2 text-gray-700">Submit a new venue for our inspectors to grade.</p>
            <Button asChild className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="member-nominate-btn"><Link to="/nominate">Start nomination</Link></Button>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-xl">
          <CardContent className="p-8">
            <div className="w-12 h-12 rounded-lg bg-red-500 text-white flex items-center justify-center"><Heart className="w-6 h-6"/></div>
            <h2 className="mt-4 font-heading text-2xl font-semibold">Saved places</h2>
            <p className="mt-2 text-gray-700">Your bookmarked accessible venues, ready when you plan a visit.</p>
            <Button asChild variant="outline" className="mt-4 rounded-full border-2 border-gray-900" data-testid="member-saved-btn"><Link to="/saved">View saved</Link></Button>
          </CardContent>
        </Card>
        <Card className="border border-gray-200 rounded-xl">
          <CardContent className="p-8">
            <div className="w-12 h-12 rounded-lg bg-gray-900 text-white flex items-center justify-center"><MapPinned className="w-6 h-6"/></div>
            <h2 className="mt-4 font-heading text-2xl font-semibold">Trip lists</h2>
            <p className="mt-2 text-gray-700">Bundle saved places into named, shareable trips.</p>
            <Button asChild variant="outline" className="mt-4 rounded-full border-2 border-gray-900" data-testid="member-trips-btn"><Link to="/trips">Open trips</Link></Button>
          </CardContent>
        </Card>
      </div>
      <div className="mt-6">
        <Link to="/claims" className="text-sm text-blue-700 hover:underline" data-testid="member-my-claims">View my venue claims →</Link>
      </div>
      <div className="mt-10">
        <DigestPreferences />
      </div>
      <div className="mt-6">
        <InviteFriendsCard />
      </div>
      <div className="mt-6">
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };
  return (
    <Card className="border border-gray-200 rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center"><KeyRound className="w-5 h-5"/></div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-semibold">Change password</h2>
            <p className="mt-1 text-sm text-gray-700">Update your Access Local password. You will stay signed in on this device.</p>
            <form onSubmit={submit} className="mt-4 grid sm:grid-cols-3 gap-3 max-w-xl">
              <input type="password" required placeholder="Current password" value={current} onChange={e => setCurrent(e.target.value)}
                className="h-11 px-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 sm:col-span-3" data-testid="pw-current"/>
              <input type="password" required minLength={6} placeholder="New password" value={next} onChange={e => setNext(e.target.value)}
                className="h-11 px-3 rounded-lg border-2 border-gray-300 focus:border-blue-600 sm:col-span-2" data-testid="pw-new"/>
              <input type="password" required placeholder="Confirm new" value={confirm} onChange={e => setConfirm(e.target.value)}
                className="h-11 px-3 rounded-lg border-2 border-gray-300 focus:border-blue-600" data-testid="pw-confirm"/>
              <Button type="submit" disabled={busy} className="rounded-full bg-gray-900 hover:bg-black sm:col-span-3 justify-self-start" data-testid="pw-submit">
                <KeyRound className="w-4 h-4 mr-2"/>{busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "member", password: "", auto: true, sendWelcome: false });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = () => { setForm({ name: "", email: "", role: "member", password: "", auto: true, sendWelcome: false }); setResult(null); setCopied(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.sendWelcome) {
        payload.send_welcome = true;
      } else if (!form.auto) {
        if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); setBusy(false); return; }
        payload.password = form.password;
      }
      const { data } = await api.post("/admin/users", payload);
      if (data.welcome_flow) {
        setResult({ email: data.email, welcome: true, emailSent: data.email_sent });
        toast.success(data.email_sent ? `Welcome email sent to ${data.email}` : `Created ${data.email} — email failed, check logs`);
      } else if (data.password) {
        setResult({ email: data.email, password: data.password });
        toast.success(`Created ${data.email} — copy the password before closing`);
      } else {
        toast.success(`Created ${data.email}`);
        setOpen(false); reset(); onCreated?.();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create user");
    } finally { setBusy(false); }
  };

  const copyPw = async () => {
    try { await navigator.clipboard.writeText(result.password); setCopied(true); toast.success("Password copied"); }
    catch { toast.error("Could not copy"); }
  };

  const close = () => { setOpen(false); if (result) onCreated?.(); reset(); };

  return (
    <Dialog open={open} onOpenChange={v => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="create-user-btn">
          <UserPlus className="w-4 h-4 mr-2"/>Create user
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{result ? "User created" : "Create a new user"}</DialogTitle>
          <DialogDescription>
            {result
              ? "Save this password now — it will not be shown again. Share it with the new user securely."
              : "Add a new member, inspector or admin. Choose auto-generate to have Access Local pick a strong password for you."}
          </DialogDescription>
        </DialogHeader>
        {result ? (
          result.welcome ? (
            <div className="space-y-3" data-testid="create-user-welcome-result">
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 text-emerald-700"/>
                  <div className="text-sm text-emerald-900">
                    {result.emailSent
                      ? <>A welcome email with a password-setup link was sent to <b>{result.email}</b>. It expires in 72 hours.</>
                      : <>Account created for <b>{result.email}</b>, but the welcome email failed to send. Check the backend logs and either resend or share a reset link manually.</>}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={close} className="rounded-full bg-gray-900 hover:bg-black" data-testid="create-user-done">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="create-user-result">
              <div className="rounded-lg border-2 border-gray-900 bg-gray-50 p-4">
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Email</div>
                <div className="font-medium">{result.email}</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-gray-500 font-semibold">One-time password</div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 font-mono text-sm bg-white border border-gray-200 rounded px-2 py-1.5 select-all" data-testid="create-user-password">{result.password}</code>
                  <Button variant="outline" size="sm" onClick={copyPw} className="rounded-full" data-testid="create-user-copy">
                    {copied ? <Check className="w-3.5 h-3.5 mr-1"/> : <Copy className="w-3.5 h-3.5 mr-1"/>}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-600">Ask the new user to sign in and change it right away using the "Change password" card on their dashboard.</p>
              <DialogFooter>
                <Button onClick={close} className="rounded-full bg-gray-900 hover:bg-black" data-testid="create-user-done">Done</Button>
              </DialogFooter>
            </div>
          )
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="cu-name">Name</Label>
              <Input id="cu-name" required value={form.name} onChange={e => set("name", e.target.value)}
                className="mt-1 border-2 border-gray-300 focus:border-blue-600" data-testid="create-user-name"/>
            </div>
            <div>
              <Label htmlFor="cu-email">Email</Label>
              <Input id="cu-email" type="email" required value={form.email} onChange={e => set("email", e.target.value)}
                className="mt-1 border-2 border-gray-300 focus:border-blue-600" data-testid="create-user-email"/>
            </div>
            <div>
              <Label htmlFor="cu-role">Role</Label>
              <Select value={form.role} onValueChange={v => set("role", v)}>
                <SelectTrigger id="cu-role" className="mt-1 border-2 border-gray-300" data-testid="create-user-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="inspector">Inspector</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border-2 border-gray-200 p-3 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.sendWelcome} onChange={e => set("sendWelcome", e.target.checked)}
                  className="mt-1" data-testid="create-user-send-welcome"/>
                <span className="text-sm">
                  <span className="inline-flex items-center gap-1 font-medium"><Mail className="w-3.5 h-3.5"/>Email the user a password-setup link</span>
                  <span className="block text-xs text-gray-600 mt-0.5">They pick their own password — nothing gets shared over chat. Link expires in 72 hours.</span>
                </span>
              </label>
              {!form.sendWelcome && (
                <>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.auto} onChange={e => set("auto", e.target.checked)}
                      className="mt-1" data-testid="create-user-auto"/>
                    <span className="text-sm">
                      <span className="inline-flex items-center gap-1 font-medium"><Sparkles className="w-3.5 h-3.5"/>Auto-generate a strong password</span>
                      <span className="block text-xs text-gray-600 mt-0.5">You'll see it once, right after creation. The user can change it after signing in.</span>
                    </span>
                  </label>
                  {!form.auto && (
                    <div>
                      <Label htmlFor="cu-pw">Password</Label>
                      <Input id="cu-pw" type="text" minLength={6} required value={form.password} onChange={e => set("password", e.target.value)}
                        className="mt-1 border-2 border-gray-300 focus:border-blue-600 font-mono" data-testid="create-user-password-input"
                        placeholder="At least 6 characters"/>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} className="rounded-full" data-testid="create-user-cancel">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="create-user-submit">
                {busy ? "Creating…" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserAction({ user, currentUserId, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const isSelf = user.id === currentUserId;
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}`);
      const cascade = data.cascade || {};
      const detail = Object.entries(cascade).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ");
      toast.success(detail ? `Deleted ${data.deleted_email} · cleaned up ${detail}` : `Deleted ${data.deleted_email}`);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user");
    } finally { setBusy(false); }
  };
  if (isSelf) {
    return <span className="text-xs text-gray-400" data-testid={`delete-user-self-${user.id}`}>You</span>;
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${user.id}`} title={`Delete ${user.email}`}>
          <Trash2 className="w-4 h-4"/>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this user?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes <b>{user.email}</b>. Their saves, invites, trips, claims and re-inspection requests will be deleted. Any venues they nominated will stay live but the nomination attribution will be removed. Inspections they carried out are kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`delete-user-cancel-${user.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={del} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid={`delete-user-confirm-${user.id}`}>
            {busy ? "Deleting…" : "Delete permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}



function InviteFriendsCard() {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");
  const load = () => api.get("/invites/mine").then(r => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const copy = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      toast.success("Invite link copied");
      setTimeout(() => setCopiedCode(""), 2000);
    } catch { toast.error("Could not copy link"); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invites");
      await load();
      await copy(data.share_url, data.code);
    } catch { toast.error("Could not create invite"); }
    finally { setBusy(false); }
  };
  const usedCount = invites.filter(i => i.used).length;
  return (
    <Card className="border border-gray-200 rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><Gift className="w-5 h-5"/></div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-heading text-xl font-semibold">Invite a friend</h2>
              {invites.length > 0 && (
                <span className="text-xs text-gray-600" data-testid="invite-summary">{usedCount}/{invites.length} invites accepted</span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-700">Share Access Local with someone who cares about accessibility. When they join through your link, they see who invited them.</p>
            <Button onClick={create} disabled={busy} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="invite-create-btn">
              <Gift className="w-4 h-4 mr-2"/>{busy ? "Creating…" : "Generate invite link"}
            </Button>
            {invites.length > 0 && (
              <ul className="mt-5 space-y-2" data-testid="invite-list">
                {invites.map(inv => (
                  <li key={inv.code} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${inv.used ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-white"}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-gray-900 truncate">{inv.share_url}</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {inv.used
                          ? <span className="text-emerald-800">Accepted by <b>{inv.used_by_name || "a new member"}</b></span>
                          : <span>Waiting for someone to join</span>}
                      </div>
                    </div>
                    {!inv.used && (
                      <Button variant="outline" size="sm" onClick={() => copy(inv.share_url, inv.code)} className="rounded-full" data-testid={`invite-copy-${inv.code}`}>
                        {copiedCode === inv.code ? <Check className="w-3.5 h-3.5 mr-1"/> : <Copy className="w-3.5 h-3.5 mr-1"/>}
                        {copiedCode === inv.code ? "Copied" : "Copy"}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DigestPreferences() {
  const [freq, setFreq] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/preferences").then(r => setFreq(r.data.digest_frequency || "weekly")); }, []);
  const save = async (val) => {
    setBusy(true);
    try {
      await api.patch("/me/preferences", { digest_frequency: val });
      setFreq(val);
      toast.success(val === "off" ? "Digest emails paused" : `Digest set to ${val}`);
    } catch (e) { toast.error("Could not save preference"); }
    finally { setBusy(false); }
  };
  if (freq === null) return null;
  const opts = [
    { v: "off", label: "Off" },
    { v: "daily", label: "Daily" },
    { v: "weekly", label: "Weekly" },
    { v: "monthly", label: "Monthly" },
  ];
  return (
    <Card className="border border-gray-200 rounded-xl">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center"><ClipboardList className="w-5 h-5"/></div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-semibold">Email digest</h2>
            <p className="mt-1 text-sm text-gray-700">Get a summary of new Diamond places and updates on the venues you have nominated. Pick a cadence — or switch it off entirely.</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Digest frequency">
              {opts.map(o => {
                const active = freq === o.v;
                return (
                  <button key={o.v} type="button" role="radio" aria-checked={active}
                    disabled={busy}
                    onClick={() => save(o.v)}
                    data-testid={`digest-freq-${o.v}`}
                    className={`px-3 py-2 rounded-full border-2 text-sm font-medium transition-colors ${active ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-800 hover:border-gray-900"}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InspectorDashboard() {
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [venues, setVenues] = useState([]);
  useEffect(() => {
    api.get("/inspections?mine=true").then(r => setInspections(r.data));
    api.get("/venues?status=all&limit=200").then(r => setVenues(r.data));
  }, []);

  const venueName = (id) => venues.find(v => v.id === id)?.venue_name || "—";

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <h1 className="font-heading text-4xl font-bold text-gray-900">Inspector — {user.name}</h1>
      <p className="mt-2 text-gray-700">Grade venues, save drafts and return to complete them later.</p>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Stat label="My inspections" value={inspections.length} icon={<ClipboardCheck className="w-5 h-5"/>}/>
        <Stat label="Drafts" value={inspections.filter(i => i.status === "draft").length} icon={<FileSearch className="w-5 h-5"/>}/>
        <Stat label="Submitted" value={inspections.filter(i => i.status === "submitted").length} icon={<ClipboardCheck className="w-5 h-5"/>}/>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl font-semibold">My inspections</h2>
          <Button asChild variant="outline" className="rounded-full border-2 border-gray-900"><Link to="/search">Find a venue to inspect</Link></Button>
        </div>
        <Card className="mt-4 rounded-xl border border-gray-200">
          <Table>
            <TableHeader><TableRow><TableHead>Venue</TableHead><TableHead>Status</TableHead><TableHead>Updated</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {inspections.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-8">No inspections yet — pick a venue from search.</TableCell></TableRow>}
              {inspections.map(i => (
                <TableRow key={i.id}>
                  <TableCell className="font-medium">{venueName(i.venue_id)}</TableCell>
                  <TableCell><Badge variant={i.status === "draft" ? "outline" : "secondary"} className="rounded-full">{i.status}</Badge></TableCell>
                  <TableCell className="text-sm text-gray-600">{new Date(i.updated_at).toLocaleString()}</TableCell>
                  <TableCell><Button asChild size="sm" variant="ghost" data-testid={`open-inspection-${i.id}`}><Link to={`/inspection/${i.id}`}>Open</Link></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <div className="mt-10">
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const [stats, setStats] = useState(null);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [logFrom, setLogFrom] = useState("");
  const [logTo, setLogTo] = useState("");
  const [logArea, setLogArea] = useState("");
  const [resetting, setResetting] = useState(false);

  const reload = () => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/venues?status=all&limit=200").then(r => setVenues(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
    api.get("/meta/areas").then(r => setAreas(r.data));
  };
  useEffect(reload, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/venues/${id}`, { status }); toast.success(`Venue ${status}`); reload(); }
    catch { toast.error("Failed to update"); }
  };
  const setRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); reload(); }
    catch { toast.error("Failed to update role"); }
  };

  const resetDemo = async () => {
    const confirm = window.prompt('This will wipe all venues, inspections, saves, trips, and claims, then re-seed sample data. Users, areas and templates are kept.\n\nType RESET to confirm.');
    if (confirm !== "RESET") return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirm: "RESET" });
      const total = Object.values(data.deleted || {}).reduce((n, x) => n + x, 0);
      toast.success(`Reset complete — removed ${total} record(s) and re-seeded venues`);
      reload();
    } catch (e) {
      toast.error("Reset failed");
    } finally { setResetting(false); }
  };

  const consentLogUrl = () => {
    const qs = new URLSearchParams();
    if (logFrom) qs.set("from", logFrom);
    if (logTo) qs.set("to", logTo);
    if (logArea) qs.set("area", logArea);
    const s = qs.toString();
    return `${API}/admin/consent-log${s ? "?" + s : ""}`;
  };

  const catName = (slug) => CATEGORIES.find(c => c.slug === slug)?.short || slug;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="font-heading text-4xl font-bold text-gray-900">Admin Console</h1>
      <p className="mt-2 text-gray-700">Full management of venues, users and inspections.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild variant="outline" className="rounded-full border-2 border-gray-900" data-testid="admin-templates-btn"><Link to="/admin/templates"><ClipboardList className="w-4 h-4 mr-2"/>Grading templates</Link></Button>
        <Button asChild variant="outline" className="rounded-full border-2 border-gray-900" data-testid="admin-claims-btn"><Link to="/admin/claims"><ShieldCheck className="w-4 h-4 mr-2"/>Owner claims</Link></Button>
        <Button asChild variant="outline" className="rounded-full border-2 border-gray-900" data-testid="admin-areas-btn"><Link to="/admin/areas"><MapPin className="w-4 h-4 mr-2"/>Manage areas</Link></Button>
        <Button onClick={resetDemo} disabled={resetting} variant="outline" className="rounded-full border-2 border-red-600 text-red-700 hover:bg-red-50" data-testid="admin-reset-btn">
          <RotateCcw className="w-4 h-4 mr-2"/>{resetting ? "Resetting…" : "Reset demo data"}
        </Button>
      </div>

      <Card className="mt-6 border-2 border-gray-900 rounded-xl">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileDown className="w-4 h-4 text-blue-700"/>
            <h2 className="font-heading text-lg font-semibold">Consent log export</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">From</label>
              <input type="date" value={logFrom} onChange={e => setLogFrom(e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-lg border-2 border-gray-300 focus:border-blue-600" data-testid="consent-log-from"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">To</label>
              <input type="date" value={logTo} onChange={e => setLogTo(e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-lg border-2 border-gray-300 focus:border-blue-600" data-testid="consent-log-to"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Area</label>
              <Select value={logArea || "all"} onValueChange={v => setLogArea(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1 h-11 border-2 border-gray-300" data-testid="consent-log-area"><SelectValue placeholder="All areas"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button asChild className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="consent-log-download">
                <a href={consentLogUrl()} target="_blank" rel="noreferrer"><FileDown className="w-4 h-4 mr-2"/>Download CSV</a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4">
        <Stat label="Users" value={stats?.users ?? "—"} icon={<Users className="w-5 h-5"/>}/>
        <Stat label="Venues" value={stats?.venues ?? "—"} icon={<FileSearch className="w-5 h-5"/>}/>
        <Stat label="Pending" value={stats?.pending ?? "—"} icon={<ClipboardCheck className="w-5 h-5"/>}/>
        <Stat label="Approved" value={stats?.approved ?? "—"} icon={<ClipboardCheck className="w-5 h-5"/>}/>
        <Stat label="Inspections" value={stats?.inspections ?? "—"} icon={<ClipboardCheck className="w-5 h-5"/>}/>
        <Stat label="Drafts" value={stats?.drafts ?? "—"} icon={<FileSearch className="w-5 h-5"/>}/>
      </div>

      <h2 className="mt-10 font-heading text-2xl font-semibold">Venues</h2>
      <Card className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Area</TableHead><TableHead>Status</TableHead><TableHead>Grade</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {venues.map(v => (
              <TableRow key={v.id}>
                <TableCell className="font-medium"><Link to={`/venue/${v.id}`} className="hover:underline">{v.venue_name}</Link></TableCell>
                <TableCell className="text-sm">{catName(v.category)}</TableCell>
                <TableCell className="text-sm">{v.area}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{v.status}</Badge></TableCell>
                <TableCell>{(v.rating_count || 0) > 0 || v.tier
                  ? <TierBadge points={v.rating} tier={v.tier} />
                  : <span className="text-xs text-gray-500">—</span>}</TableCell>
                <TableCell className="space-x-2">
                  {v.status !== "approved" && <Button size="sm" onClick={() => setStatus(v.id, "approved")} data-testid={`approve-${v.id}`} className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>}
                  {v.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => setStatus(v.id, "rejected")} data-testid={`reject-${v.id}`}>Reject</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="mt-10 flex items-center justify-between gap-3">
        <h2 className="font-heading text-2xl font-semibold">Users</h2>
        <CreateUserDialog onCreated={reload} />
      </div>
      <Card className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="w-14 text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={v => setRole(u.id, v)}>
                    <SelectTrigger className="w-36 h-9" data-testid={`role-select-${u.id}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="inspector">Inspector</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <DeleteUserAction user={u} currentUserId={currentUserId} onDeleted={reload} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="mt-10">
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <Card className="rounded-xl border border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-gray-600 text-sm">{icon}{label}</div>
        <div className="mt-1 font-heading text-3xl font-bold text-gray-900">{value}</div>
      </CardContent>
    </Card>
  );
}
